import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import api from './axiosConfig';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Paper,
  IconButton,
  Chip,
  LinearProgress,
  Alert,
  AlertTitle,
  Divider,
  Badge,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  Inventory,
  Assessment,
  Warning,
  CheckCircle,
  Schedule,
  CalendarToday,
  ArrowForward,
  Refresh,
  Notifications,
  AccountCircle,
  Logout,
  HelpOutline,
  Timeline,
  Settings as SettingsIcon
} from '@mui/icons-material';

// Import modal components
import SundayPlanningInterface from './SundayPlanningInterface';
import EndOfDayPerformanceReview from './EndOfDayPerformanceReview';
import DailyForecastUpdate from './DailyForecastUpdate';
import VarianceAnalysisModal from './VarianceAnalysisModal';

const MainDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [pendingRequests, setPendingRequests] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Modal states
  const [showSundayPlanning, setShowSundayPlanning] = useState(false);
  const [showPerformanceReview, setShowPerformanceReview] = useState(false);
  const [showDailyUpdate, setShowDailyUpdate] = useState(false);
  const [showVarianceAnalysis, setShowVarianceAnalysis] = useState(false);

  useEffect(() => {
    fetchSnapshot();
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchSnapshot, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchSnapshot = async () => {
    try {
      setLoading(true);
      
      // Fetch all dashboard data in parallel
      const [forecast, staff, money, stock, pending] = await Promise.all([
        api.get('/forecast/current-week').catch(() => ({ data: { has_data: false } })),
        api.get('/employees/active-count').catch(() => ({ data: { count: 0 } })),
        api.get('/dashboard/quick_stats').catch(() => ({ data: {} })),
        api.get('/stock/summary').catch(() => ({ data: {} })),
        api.get('/dashboard/pending_requests').catch(() => ({ data: { pending_requests: 0 } }))
      ]);

      setSnapshot({
        forecast: forecast.data,
        staff: staff.data,
        money: money.data,
        stock: stock.data
      });
      
      setPendingRequests(pending.data.pending_requests || 0);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching snapshot:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshForecastData = async () => {
    try {
      const response = await api.get('/forecast/current-week');
      setSnapshot(prev => ({
        ...prev,
        forecast: response.data
      }));
    } catch (error) {
      console.error('Error refreshing forecast data:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleUserMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-GB', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Health Status Component
  const HealthStatus = ({ status, label }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'good': return 'success';
        case 'warning': return 'warning';
        case 'critical': return 'error';
        default: return 'default';
      }
    };

    const getStatusIcon = () => {
      switch (status) {
        case 'good': return <CheckCircle fontSize="small" />;
        case 'warning': return <Warning fontSize="small" />;
        case 'critical': return <Warning fontSize="small" />;
        default: return <Schedule fontSize="small" />;
      }
    };

    return (
      <Chip
        icon={getStatusIcon()}
        label={label}
        color={getStatusColor()}
        size="small"
        variant="outlined"
      />
    );
  };

  // Weekly Forecast Snapshot Card
  const ForecastSnapshot = () => {
    const hasData = snapshot?.forecast?.has_data;
    const progress = snapshot?.forecast?.weekly_progress || 0;
    const status = progress >= 80 ? 'good' : progress >= 60 ? 'warning' : 'critical';

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TrendingUp color="primary" />
              <Typography variant="h6" fontWeight="bold">
                This Week's Forecast
              </Typography>
            </Box>
            <HealthStatus status={hasData ? status : 'default'} label={hasData ? 'Active' : 'Not Set'} />
          </Box>

          {hasData ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h3" fontWeight="bold" color="primary">
                  {progress}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  of weekly target achieved
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mb: 2,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: progress >= 80 ? 'success.main' : progress >= 60 ? 'warning.main' : 'error.main'
                  }
                }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Actual: {formatCurrency(snapshot.forecast.total_actual)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Target: {formatCurrency(snapshot.forecast.total_forecast)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Quick Actions:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                  onClick={() => navigate('/forecast-dashboard')}
                >
                  This Week Performance
                </Button>
                <Button
                  size="small"
                  variant="text"
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                  onClick={() => setShowSundayPlanning(true)}
                >
                  Set Next Week Forecast
                </Button>
                <Button
                  size="small"
                  variant="text"
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                  onClick={() => setShowPerformanceReview(true)}
                >
                  Review Completed Days
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>No Forecast Set</AlertTitle>
                Create a Sunday plan to start tracking
              </Alert>
              <Button
                fullWidth
                variant="contained"
                onClick={() => setShowSundayPlanning(true)}
              >
                Create Sunday Plan
              </Button>
            </>
          )}

          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/forecast-dashboard')}
            sx={{ mt: 2 }}
          >
            View Full Forecast
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Staff Snapshot Card
  const StaffSnapshot = () => {
    const activeCount = snapshot?.staff?.count || 0;
    const hasStaff = activeCount > 0;

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <People color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Team
              </Typography>
            </Box>
            <HealthStatus status={hasStaff ? 'good' : 'warning'} label={hasStaff ? 'Active' : 'Setup'} />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {activeCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              active team members
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Holiday Requests:
              </Typography>
              <Chip label={`${pendingRequests} Pending`} size="small" color={pendingRequests > 0 ? 'warning' : 'default'} />
            </Box>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/employees')}
            sx={{ mt: 2 }}
          >
            Manage Team
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Custom Pound Icon Component for Cards
  const PoundIconCard = ({ sx, ...props }) => (
    <Box sx={{ 
      fontWeight: 'bold', 
      fontSize: '1.5rem', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      width: '1em',
      height: '1em',
      color: 'primary.main',
      ...sx 
    }} {...props}>
      £
    </Box>
  );

  // Money Snapshot Card
  const MoneySnapshot = () => {
    const monthlyRevenue = parseFloat(snapshot?.money?.monthlyRevenue || 0);
    const yesterdayRevenue = parseFloat(snapshot?.money?.yesterdayRevenue || 0);
    const targetProgress = parseFloat(snapshot?.money?.targetProgress || 0);
    const hasRevenue = monthlyRevenue > 0;

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PoundIconCard />
              <Typography variant="h6" fontWeight="bold">
                Revenue
              </Typography>
            </Box>
            <HealthStatus 
              status={targetProgress >= 80 ? 'good' : targetProgress >= 60 ? 'warning' : 'critical'} 
              label={hasRevenue ? 'Tracking' : 'No Data'} 
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {formatCurrency(monthlyRevenue)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              this month
            </Typography>
          </Box>

          {targetProgress > 0 && (
            <LinearProgress
              variant="determinate"
              value={targetProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                mb: 2,
                bgcolor: 'grey.200'
              }}
            />
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Yesterday:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(yesterdayRevenue)}
              </Typography>
            </Box>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/money')}
            sx={{ mt: 2 }}
          >
            View Finance
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Stock Snapshot Card
  const StockSnapshot = () => {
    const lowStockCount = snapshot?.stock?.low_stock_count || 0;
    const totalProducts = snapshot?.stock?.total_products || 0;
    const hasStock = totalProducts > 0;

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Stock
              </Typography>
            </Box>
            <HealthStatus 
              status={lowStockCount === 0 ? 'good' : lowStockCount < 5 ? 'warning' : 'critical'} 
              label={hasStock ? 'Tracked' : 'Setup'} 
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h3" fontWeight="bold" color="primary">
              {totalProducts}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              products tracked
            </Typography>
          </Box>

          {lowStockCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>{lowStockCount} items low</AlertTitle>
              Reorder soon
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Stock Value:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {formatCurrency(snapshot?.stock?.total_value || 0)}
              </Typography>
            </Box>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowForward />}
            onClick={() => navigate('/stock')}
            sx={{ mt: 2 }}
          >
            Manage Stock
          </Button>
        </CardContent>
      </Card>
    );
  };

  // Getting Started Checklist
  const GettingStartedChecklist = () => {
    const gettingStartedItems = [
      { 
        task: 'Add your first employee', 
        completed: (snapshot?.staff?.count && parseInt(snapshot.staff.count) > 0) || false,
        action: () => navigate('/employees')
      },
      { 
        task: 'Set your first weekly forecast', 
        completed: snapshot?.forecast?.has_data || false,
        action: () => setShowSundayPlanning(true)
      },
      { 
        task: 'Upload your product list', 
        completed: (snapshot?.stock?.total_products && parseInt(snapshot.stock.total_products) > 0) || false,
        action: () => navigate('/stock')
      },
      { 
        task: 'Record your first day\'s takings', 
        completed: (snapshot?.money?.yesterdayRevenue && parseFloat(snapshot.money.yesterdayRevenue) > 0) || false,
        action: () => navigate('/money')
      }
    ];

    const completedCount = gettingStartedItems.filter(item => item.completed).length;
    const progress = (completedCount / gettingStartedItems.length) * 100;

    return (
      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          <Box sx={{ 
            bgcolor: 'primary.light', 
            p: 2, 
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <HelpOutline sx={{ fontSize: 32, color: 'primary.main' }} />
          </Box>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Getting Started
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Complete these steps to get the most out of your system
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {completedCount} of {gettingStartedItems.length} completed
                </Typography>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  {Math.round(progress)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={progress}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            
            <Grid container spacing={2}>
              {gettingStartedItems.map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2, 
                      p: 2, 
                      bgcolor: item.completed ? 'success.light' : 'grey.50', 
                      borderRadius: 2,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: item.completed ? 'success.light' : 'grey.100'
                      }
                    }}
                    onClick={item.action}
                  >
                    <CheckCircle 
                      sx={{ 
                        color: item.completed ? 'success.main' : 'grey.400',
                        fontSize: 24 
                      }} 
                    />
                    <Typography 
                      variant="body2"
                      sx={{ 
                        textDecoration: item.completed ? 'line-through' : 'none',
                        color: item.completed ? 'text.secondary' : 'text.primary',
                        fontWeight: item.completed ? 'normal' : 'medium'
                      }}
                    >
                      {item.task}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <Typography>Loading your dashboard...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          bgcolor: 'white', 
          color: 'text.primary',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5" fontWeight="bold" color="primary">
              Salon Logic Pro
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showAdvanced}
                  onChange={(e) => setShowAdvanced(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={<Typography variant="body2">Advanced</Typography>}
            />
            
            <IconButton color="inherit" onClick={() => navigate('/notifications')}>
              <Badge badgeContent={pendingRequests || 0} color="error">
                <Notifications />
              </Badge>
            </IconButton>
            
            <Button
              onClick={handleUserMenuClick}
              startIcon={<AccountCircle />}
              sx={{ textTransform: 'none', color: 'text.primary' }}
            >
              {user?.email?.split('@')[0]}
            </Button>
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleUserMenuClose}
            >
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/personal_info'); }}>
                Profile Settings
              </MenuItem>
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings/company'); }}>
                Company Settings
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleUserMenuClose(); handleLogout(); }}>
                <Logout sx={{ mr: 1, fontSize: 20 }} />
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ p: 3 }}>
        {/* Welcome Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h4" fontWeight="bold">
              Business Snapshot
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Last updated: {formatTime(lastUpdated)}
              </Typography>
              <IconButton onClick={fetchSnapshot} size="small">
                <Refresh />
              </IconButton>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => navigate('/settings/company')}
                sx={{ 
                  borderColor: '#e0e0e0',
                  color: 'text.secondary',
                  '&:hover': { 
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    bgcolor: 'primary.light'
                  }
                }}
              >
                Company Settings
              </Button>
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Quick overview of your salon's performance
          </Typography>
        </Box>

        {/* Main Grid */}
        <Grid container spacing={3}>
          {/* Top Row - Key Metrics */}
          <Grid item xs={12} md={6} lg={3}>
            <ForecastSnapshot />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StaffSnapshot />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MoneySnapshot />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <StockSnapshot />
          </Grid>

          {/* Bottom Row - Getting Started & Quick Links */}
          <Grid item xs={12}>
            <GettingStartedChecklist />
          </Grid>

          {showAdvanced && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Advanced Forecasting Tools
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="contained"
                      startIcon={<CalendarToday />}
                      onClick={() => setShowSundayPlanning(true)}
                    >
                      Sunday Planning
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Assessment />}
                      onClick={() => setShowPerformanceReview(true)}
                    >
                      Performance Review
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<TrendingUp />}
                      onClick={() => setShowDailyUpdate(true)}
                    >
                      Update Tomorrow's Forecast
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<Timeline />}
                      onClick={() => setShowVarianceAnalysis(true)}
                    >
                      Variance Analysis
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Modal Components */}
      {showSundayPlanning && (
        <SundayPlanningInterface
          onClose={() => setShowSundayPlanning(false)}
          onSave={() => {
            refreshForecastData();
            setShowSundayPlanning(false);
          }}
        />
      )}

      {showPerformanceReview && (
        <EndOfDayPerformanceReview
          onClose={() => setShowPerformanceReview(false)}
        />
      )}

      {showDailyUpdate && (
        <DailyForecastUpdate
          open={showDailyUpdate}
          onClose={() => setShowDailyUpdate(false)}
          onSave={() => {
            refreshForecastData();
            setShowDailyUpdate(false);
          }}
        />
      )}

      {showVarianceAnalysis && (
        <VarianceAnalysisModal
          open={showVarianceAnalysis}
          onClose={() => setShowVarianceAnalysis(false)}
        />
      )}
    </Box>
  );
};

export default MainDashboard;